import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface RegisterFormProps {
  onSuccess?: (response: any) => void;
  onFailure?: (message: string, conflictType?: string, existingUsername?: string) => void;
  onSwitchToLogin?: () => void;
  onClose?: () => void;
}

const RegisterForm: React.FC<RegisterFormProps> = ({ onSuccess, onFailure, onSwitchToLogin, onClose }) => {
  const [conflictInfo, setConflictInfo] = useState<{type: string, existingUsername?: string} | null>(null);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { register } = useAuth();

  const clearConflictInfo = () => {
    if (conflictInfo) {
      setConflictInfo(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setConflictInfo(null);

    if (password !== confirmPassword) {
      onFailure?.('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      onFailure?.('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);

    try {
      const response = await register(username, email, password);
      if (response.success) {
        // Add the email to the response so we can use it in the parent component
        setConflictInfo(null);
        onSuccess?.({ ...response, email });
      } else {
        // Check if this is a conflict error with specific type
        if (response.conflictType) {
          setConflictInfo({ 
            type: response.conflictType, 
            existingUsername: response.existingUsername 
          });
        }
        onFailure?.(response.message || 'Registration failed', response.conflictType, response.existingUsername);
      }
    } catch (err) {
      setConflictInfo(null);
      onFailure?.(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="auth-form">
      {onClose && (
        <button className="popup-close-btn" onClick={onClose}>✕</button>
      )}
      <h2>Register</h2>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="username">Username:</label>
          <div className="input-container">
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => { setUsername(e.target.value); clearConflictInfo(); }}
              required
              disabled={loading}
              minLength={3}
            />
            <div className="validation-tooltip">
              <strong>Username Requirements:</strong>
              <ul>
                <li>3-50 characters long</li>
                <li>Letters, numbers, and underscores only</li>
                <li>No spaces or special characters</li>
              </ul>
            </div>
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="email">Email:</label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); clearConflictInfo(); }}
            required
            disabled={loading}
          />
        </div>
        <div className="form-group">
          <label htmlFor="password">Password:</label>
          <div className="input-container">
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                minLength={6}
                style={{ paddingRight: '40px' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  color: '#666',
                  padding: '4px'
                }}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            <div className="validation-tooltip">
              <strong>Password Requirements:</strong>
              <ul>
                <li>At least 6 characters long</li>
                <li>Can include letters, numbers, and symbols</li>
                <li>Choose a strong, unique password</li>
              </ul>
            </div>
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="confirmPassword">Confirm Password:</label>
          <div className="input-container">
            <div style={{ position: 'relative' }}>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
                minLength={6}
                style={{ paddingRight: '40px' }}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  color: '#666',
                  padding: '4px'
                }}
              >
                {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            <div className="validation-tooltip">
              <strong>Confirm Password:</strong>
              <ul>
                <li>Must match the password above</li>
                <li>Enter the same password to confirm</li>
              </ul>
            </div>
          </div>
        </div>
        <button type="submit" disabled={loading}>
          {loading ? 'Creating Account...' : 'Register'}
        </button>
      </form>
      
      {/* Specific conflict messaging */}
      {conflictInfo && (
        <div style={{ 
          marginTop: '1rem', 
          padding: '1rem', 
          backgroundColor: '#fff3cd', 
          border: '1px solid #ffeaa7', 
          borderRadius: '4px',
          fontSize: '0.9rem'
        }}>
          {conflictInfo.type === 'username' ? (
            <div>
              <strong>Username already taken!</strong>
              <p style={{ margin: '0.5rem 0 0 0' }}>Please choose a different username.</p>
            </div>
          ) : conflictInfo.type === 'email' ? (
            <div>
              <strong>Email already registered!</strong>
              <p style={{ margin: '0.5rem 0' }}>You can either:</p>
              <div style={{ marginLeft: '1rem' }}>
                <p style={{ margin: '0.25rem 0' }}>1. Use a different email address, or</p>
                <p style={{ margin: '0.25rem 0' }}>
                  2. <button 
                      type="button" 
                      className="link-button" 
                      onClick={onSwitchToLogin}
                      style={{ padding: 0, fontSize: 'inherit', textDecoration: 'underline' }}
                    >
                      Login with your existing account
                    </button>
                    {conflictInfo.existingUsername && (
                      <span style={{ color: '#666' }}> (username: {conflictInfo.existingUsername})</span>
                    )}
                </p>
              </div>
            </div>
          ) : null}
        </div>
      )}
      
      <p style={{marginTop: '1.5rem'}}>
        Already have an account?{' '}
        <button 
          type="button" 
          className="link-button" 
          onClick={onSwitchToLogin}
          disabled={loading}
        >
          Login here
        </button>
      </p>
    </div>

  </>);
};

export default RegisterForm;